import ExcelJS from "exceljs";
import FileSaver from "file-saver";

/**
 * Transforms rate card API response to flat array for export
 * @param {Array} rateCards - Array of rate card objects from API
 * @returns {Array} flatRows - Array of flat objects for export
 */
const EXCEL_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const EXCEL_EXTENSION = ".xlsx";
export const RATE_CARD_EXPORT_SHEET_NAME = "RateCard";

const sanitizeExportValue = (value) => {
  if (typeof value !== "string") {
    return value;
  }
  return value.replace(/<script.*?>.*?<\/script>/gi, "").trim();
};

const getExportCellValue = (row, field) => {
  let raw = row[field] ?? "";
  if (typeof raw === "object" && raw !== null) {
    raw = JSON.stringify(raw);
  }
  return sanitizeExportValue(String(raw));
};

export function transformRateCardsForExport(rateCards) {
  const flatRows = [];
  if (!Array.isArray(rateCards)) return flatRows;
  rateCards.forEach((rateCard) => {
    const {
      rateCardName,
      currency,
      priceType,
      revShareType,
      windowSequence,
      windows,
      tierAliases,
      createdAt,
      modifiedAt,
      createdBy,
      modifiedBy,
      isTpr,
    } = rateCard;
    const windowsList = (windows || []).map((item) => ({
      windowId: item.id.tapWindowId,
      windowName: item.window.windowName,
      percentage: item.percentage,
    }));
    if (tierAliases && tierAliases.length > 0) {
      tierAliases.forEach((alias) => {
        (alias.tiers || []).forEach((tierObj) => {
          const window = windowsList.find(
            (it) => it.windowId === tierObj.id.tapWindowId,
          );
          if (window) {
            flatRows.push({
              rateCardName,
              currency,
              priceType,
              revShareType,
              windowSequenceName: windowSequence?.windowSequenceName || "",
              windowName: window.windowName,
              percentage: window.percentage,
              tierName: alias.tierAliasName,
              tierFormat: (alias.tierAliasFormat || "").replaceAll(",", " | "),
              tierNumber: tierObj.id.tierNumber,
              tier: tierObj.tierName,
              price: tierObj.price,
              isTpr: isTpr === true ? "Yes" : "No",
              createdBy: createdBy?.fullName || "",
              createdAt,
              modifiedBy: modifiedBy?.fullName || "",
              modifiedAt,
            });
          }
        });
      });
    } else {
      windowsList.forEach((window) => {
        flatRows.push({
          rateCardName,
          currency,
          priceType,
          revShareType,
          windowSequenceName: windowSequence?.windowSequenceName || "",
          windowName: window.windowName,
          percentage: window.percentage,
          tierName: "",
          tierFormat: "",
          tierNumber: "",
          tier: "",
          price: "",
          isTpr: isTpr === true ? "Yes" : "No",
          createdBy: createdBy?.fullName || "",
          createdAt,
          modifiedBy: modifiedBy?.fullName || "",
          modifiedAt,
        });
      });
    }
  });
  return flatRows;
}

/**
 * Loads full rate card records needed for CSV export.
 * The list endpoint only returns summary rows; detail is on GET /rateCard/:id.
 * @param {Array<number|string>} ids - Rate card IDs to export
 * @param {Function} rateCardById - API function that loads a rate card by ID
 * @returns {Promise<Array>} Full rate card records suitable for export
 */
export async function fetchFullRateCardsForExport(ids, rateCardById) {
  if (!Array.isArray(ids) || ids.length === 0) {
    return [];
  }

  const results = await Promise.all(ids.map((id) => rateCardById(id)));

  return results.filter(
    (rateCard) =>
      rateCard && !rateCard.code && !rateCard.httpCode && !rateCard.error,
  );
}

export const headers = [
  "rateCardName",
  "currency",
  "priceType",
  "revShareType",
  "windowSequenceName",
  "windowName",
  "percentage",
  "tierName",
  "tierFormat",
  "tierNumber",
  "tier",
  "price",
  "isTpr",
  "createdBy",
  "createdAt",
  "modifiedBy",
  "modifiedAt",
];

export const rename = [
  "Rate Card Name",
  "Currency",
  "Price Type",
  "Rev Share Type",
  "Window Sequence Name",
  "Window",
  "Rev Share Value",
  "Tier Name",
  "Tier Format",
  "Tier Number",
  "Tier",
  "Tier Price",
  "TPR",
  "Created By",
  "Created At",
  "Modified By",
  "Modified At",
];

/**
 * Exports rate card rows to Excel with a fixed worksheet name.
 * @param {Array} data - Flattened rate card rows for export
 * @param {string} [fileName="ratecards"] - Download file name without extension
 * @returns {Promise<void>}
 */
export async function exportRateCardsToExcel(data, fileName = "ratecards") {
  if (!data || data.length === 0) {
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(RATE_CARD_EXPORT_SHEET_NAME);

  sheet.columns = headers.map((field, index) => ({
    header: rename[index],
    key: field,
  }));

  for (const row of data) {
    const rowObj = {};
    for (const field of headers) {
      rowObj[field] = getExportCellValue(row, field);
    }
    sheet.addRow(rowObj);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: EXCEL_TYPE });
  FileSaver.saveAs(blob, `${fileName}${EXCEL_EXTENSION}`);
}
